/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import toast from "react-hot-toast"
import {
  selectAdasProgramsMetaData,
  selectSensingSystemRequirementsTableDataSetStatusMap,
  selectSensingSystemRequirementsTableRows,
  selectSensingSystemRequirementsTableSelectedRows,
  selectSensingSystemRequirementsUpdateReportDialogOpen,
  selectSensorComponentRequirementsTableDataSetStatusMap,
  selectSensorComponentRequirementsTableRows,
  selectSensorComponentRequirementsTableSelectedRows,
  selectSensorComponentRequirementsUpdateReportDialogOpen,
  selectWorldModelSolutionRequirementsTableDataSetStatusMap,
  selectWorldModelSolutionRequirementsTableRows,
  selectWorldModelSolutionRequirementsTableSelectedRows,
  selectWorldModelSolutionRequirementsUpdateReportDialogOpen,
  setSensingSystemRequirementsUpdateReportDialogOpen,
  setSensorComponentRequirementsUpdateReportDialogOpen,
  setWorldModelSolutionRequirementsUpdateReportDialogOpen
} from "../../../../../../redux/ducks/AdasPrograms/slice"
import {useAppDispatch, useAppSelector} from "../../../../../../redux/hooks"
import ArgosDialog from "../../../../../components/Dialogs/ArgosDialog"
import {AdasProgramsReportPageIdx, AdasProgramsReportPages, EUpdateReportsStatus, GreenLineReportsTableData, RedLineReportsTableData} from "../constants"
import {usePostRequest} from "../../../../../../api/helper"
import {CAdasProgramsRequests} from "../../types"
import {EArgosReportsState} from "../../../../../../constants/colors"
import Loader from "../../../../../components/Loader/Loader"
import ArgosFlexbox from "../../../../../components/Containers/ArgosFlexbox"
import {BulletList} from "../../../../../components/Lists/BulletList"
import ArgosButton, {ArgosLoadingButton} from "../../../../../components/Buttons/ArgosButton"
import {useEffect, useMemo, useState} from "react"
import ADAS_PROGRAMS_CONSTANTS from "../../../constants"
import {StringParam, useQueryParam} from "use-query-params"
import {getPageFromHtabIndex} from "../helpers"


export const updateReportDialogSlice = {
  [AdasProgramsReportPages.SENSING_SYSTEM_REQUIREMENT]: {
    isDialogOpen: selectSensingSystemRequirementsUpdateReportDialogOpen,
    setIsDialogOpen: setSensingSystemRequirementsUpdateReportDialogOpen,
    dataSetStatusMap: selectSensingSystemRequirementsTableDataSetStatusMap,
    selectTableRows: selectSensingSystemRequirementsTableRows,
    updateReportSummaryEndpoint: CAdasProgramsRequests.updateReportSummary.url,
    updateReportEndpoint: CAdasProgramsRequests.updateReport.url,
    selectedReports: selectSensingSystemRequirementsTableSelectedRows,
  },
  [AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT]: {
    isDialogOpen: selectSensorComponentRequirementsUpdateReportDialogOpen,
    setIsDialogOpen: setSensorComponentRequirementsUpdateReportDialogOpen,
    dataSetStatusMap: selectSensorComponentRequirementsTableDataSetStatusMap,
    selectTableRows: selectSensorComponentRequirementsTableRows,
    updateReportSummaryEndpoint: ADAS_PROGRAMS_CONSTANTS.itemDetails.sensorComponentRequirements.endpoints.updateReportSummary,
    updateReportEndpoint: ADAS_PROGRAMS_CONSTANTS.itemDetails.sensorComponentRequirements.endpoints.updateReport,
    selectedReports: selectSensorComponentRequirementsTableSelectedRows,
  },
  [AdasProgramsReportPages.WORLD_MODEL_SOLUTION_REQUIREMENT]: {
    isDialogOpen: selectWorldModelSolutionRequirementsUpdateReportDialogOpen,
    setIsDialogOpen: setWorldModelSolutionRequirementsUpdateReportDialogOpen,
    dataSetStatusMap: selectWorldModelSolutionRequirementsTableDataSetStatusMap,
    selectTableRows: selectWorldModelSolutionRequirementsTableRows,
    updateReportSummaryEndpoint: ADAS_PROGRAMS_CONSTANTS.itemDetails.worldModelSolutionRequirements.endpoints.updateReportSummary,
    updateReportEndpoint: ADAS_PROGRAMS_CONSTANTS.itemDetails.worldModelSolutionRequirements.endpoints.updateReport,
    selectedReports: selectWorldModelSolutionRequirementsTableSelectedRows,
  },
}

export const UpdateReportDialog = () => {
  const dispatch = useAppDispatch()

  const htabid = ADAS_PROGRAMS_CONSTANTS.itemDetails.horizontalTabs.pageHtabs.tabIndexUrlKey
  const [htab, setHtab] = useQueryParam(htabid, StringParam)
  const page = useMemo(() => getPageFromHtabIndex(htab as AdasProgramsReportPageIdx), [htab])

  const selectedReports = useAppSelector(updateReportDialogSlice[page].selectedReports)

  const isOpen = useAppSelector(updateReportDialogSlice[page].isDialogOpen)
  const closeDialog = () => {
    dispatch(updateReportDialogSlice[page].setIsDialogOpen(false))
  }
  const dataSetStatusMap = useAppSelector(updateReportDialogSlice[page].dataSetStatusMap)
  const tableRows = useAppSelector(updateReportDialogSlice[page].selectTableRows)

  const adasProgramMetaData = useAppSelector(selectAdasProgramsMetaData)
  const adasProgramId = adasProgramMetaData?.adasProgramId

  const [isUpdateReportsSummaryLoading, setIsUpdateReportsSummaryLoading] = useState(false)
  const [hasUpdatedReports, setHasUpdatedReports] = useState(EUpdateReportsStatus.NOT_STARTED)
  const [updateReportsSummary, setUpdateReportsSummary] = useState<UpdateReportsSummary | null>(null)
  const [isUpdateReportsLoading, setIsUpdateReportsLoading] = useState(false)

  /** Types */
  type UpdateReportsSummary = {
    'These reports will be deleted': string[]
    'These reports will be modified': string[]
  }
  type UpdateReportSummaryResponse = {
    updateReportsSummary: UpdateReportsSummary
  }

  /** Request handles */
  const updateReportsHandle = usePostRequest(updateReportDialogSlice[page].updateReportEndpoint)
  const updateReportSummaryHandle = usePostRequest(updateReportDialogSlice[page].updateReportSummaryEndpoint)

  const getUpdateReportSummary = () => {
    setIsUpdateReportsSummaryLoading(true)
    updateReportSummaryHandle({
      'requestType': 'getInfo',
      adasProgramId,
      selectedReports
    }).then((res: UpdateReportSummaryResponse) => {
      setUpdateReportsSummary(res.updateReportsSummary)
    }).catch((err) => {
      console.log('Get update reports summary failed', err)
      toast.error('Get update reports summary failed')
      closeDialog()
    }).finally(() => {
      setIsUpdateReportsSummaryLoading(false)
    })
  }

  const updateReports = () => {
    // Get reports data {id, datasetId, format). So this will update the reports that are loaded in this client instance.
    interface ReportData {
      datasetId: string;
      reportFormat: string;
      datasetState: EArgosReportsState;
    }

    const reportsData: Record<string, ReportData> = {}
    for (const row of tableRows) {
      const datasetState: EArgosReportsState = (dataSetStatusMap?.[row.reportId] ?? row.dataSetState) as EArgosReportsState

      // Only update selected reports
      if (!selectedReports[row.reportId]) {
        continue
      }

      const datasetId = page === AdasProgramsReportPages.SENSOR_COMPONENT_REQUIREMENT
        ? (row as GreenLineReportsTableData).greenLineDataSetId
        : (row as RedLineReportsTableData).redLineDataSetId
      reportsData[row.reportId] = {
        datasetId,
        reportFormat: row.reportFormat,
        datasetState
      }
    }

    setIsUpdateReportsLoading(true)
    setHasUpdatedReports(EUpdateReportsStatus.IN_PROGRESS)
    updateReportsHandle({
      'action': 'updateReport',
      adasProgramId,
      page,
      reportsData
    }).then(() => {
      setHasUpdatedReports(EUpdateReportsStatus.SUCCESS)
    }).catch((err) => {
      console.log('An error occurred while updating reports', err)
      toast.error('An error occurred while updating reports')
      setHasUpdatedReports(EUpdateReportsStatus.FAILED)
    }).finally(() => {
      setIsUpdateReportsLoading(false)
    })
  }

  useEffect(() => {
    if (adasProgramId && isOpen) {
      getUpdateReportSummary()
    }
  }, [adasProgramId, isOpen])

  return (
    <ArgosDialog
      width='auto'
      height='40vh'
      open={isOpen}
      onClose={closeDialog}
      headerTitle="Update Report"
    >
      <Loader isLoading={isUpdateReportsSummaryLoading}>
        <ArgosFlexbox vertical style={{height: '100%'}}>
          {hasUpdatedReports === EUpdateReportsStatus.SUCCESS &&
            <p style={{color: 'green', textAlign: 'center'}}>
              Reports updated successfully
            </p>
          }
          {hasUpdatedReports === EUpdateReportsStatus.FAILED &&
            <p style={{color: 'red', textAlign: 'center'}}>
              Failed to update reports
            </p>
          }
          <div style={{flex: 1, overflow: 'auto'}}>
            {updateReportsSummary && <BulletList data={updateReportsSummary}/>}
          </div>
          <ArgosFlexbox style={{justifyContent: 'center'}}>
            <ArgosButton onClick={() => closeDialog()} disabled={isUpdateReportsLoading}>
              Cancel
            </ArgosButton>
            <ArgosLoadingButton
              disabled={isUpdateReportsSummaryLoading}
              isLoading={isUpdateReportsLoading}
              onClick={() => {
                updateReports()
                closeDialog()
              }}
            >
              Update
            </ArgosLoadingButton>
          </ArgosFlexbox>
        </ArgosFlexbox>
      </Loader>
    </ArgosDialog>
  )
}
